import { matchPath } from 'react-router-dom';
import { z } from 'zod';

/** The RBAC admin page. It renders only with the RBAC permission, whatever the config says. */
export const RBAC_PAGE_ID = 'page:rbac';

/** One placed page in the sidebar config. */
export interface NavItemConfig {
  /** The page extension id, such as `page:search`. */
  id: string;
  /**
   * Extra path patterns, in react-router `matchPath` syntax, on which this item
   * is the current one, such as `/catalog/:namespace/tool/*`.
   */
  match?: string[];
  /** The sidebar label. Without it the page title shows. */
  title?: string;
}

/** A titled group of placed pages. */
export interface NavSectionConfig {
  items: NavItemConfig[];
  title: string;
}

/** The placement of the sidebar, read from the `nav-content:app` config. */
export interface NavConfig {
  /** Page ids that never render in the sidebar, while the page itself stays on. */
  hidden: string[];
  /** The header of the section with every page the config does not place. */
  otherTitle: string;
  primary: NavItemConfig[];
  sections: NavSectionConfig[];
  utility: NavItemConfig[];
}

const navItemSchema = z.object({
  id: z.string(),
  match: z.array(z.string()).optional(),
  title: z.string().optional(),
});

const navSectionSchema = z.object({
  items: z.array(navItemSchema),
  title: z.string(),
});

/**
 * The `configSchema` of the sidebar extension, one schema per top-level key.
 * Every key has a default, so a missing config renders every page in Other.
 */
export const navConfigSchema = {
  hidden: z.array(z.string()).default([]),
  otherTitle: z.string().default('Other'),
  primary: z.array(navItemSchema).default([]),
  sections: z.array(navSectionSchema).default([]),
  utility: z.array(navItemSchema).default([]),
};

/** The part of an AppNav nav item that the placement reads. */
export interface PageNavItem<TIcon> {
  href: string;
  icon: TIcon;
  node: { spec: { id: string } };
  title: string;
}

/** The part of AppNav's nav item collection that the placement uses. */
export interface PageNavItems<TIcon> {
  rest(): PageNavItem<TIcon>[];
  take(id: string): PageNavItem<TIcon> | undefined;
}

/** Either the glyph of the page icon or, when the page icon is not a glyph, the element itself. */
export type NavEntryIcon<TIcon, TGlyph> =
  | { glyph: TGlyph; kind: 'glyph' }
  | { element: TIcon; kind: 'page' };

/** One item to render in a slot. */
export interface NavEntry<TIcon, TGlyph> {
  icon: NavEntryIcon<TIcon, TGlyph>;
  id: string;
  match: readonly string[];
  title: string;
  to: string;
}

export interface NavSlotSection<TIcon, TGlyph> {
  items: NavEntry<TIcon, TGlyph>[];
  title: string;
}

export interface NavSlots<TIcon, TGlyph> {
  other: NavSlotSection<TIcon, TGlyph>;
  primary: NavEntry<TIcon, TGlyph>[];
  sections: NavSlotSection<TIcon, TGlyph>[];
  utility: NavEntry<TIcon, TGlyph>[];
}

export interface NavContext<TIcon, TGlyph> {
  canAdministerRbac: boolean;
  /** The glyph of a page icon, or `undefined` when the icon carries none. */
  findGlyph(icon: TIcon): TGlyph | undefined;
}

interface PlacementState<TIcon, TGlyph> {
  context: NavContext<TIcon, TGlyph>;
  navItems: PageNavItems<TIcon>;
  placedIds: Set<string>;
}

function isAllowed(id: string, canAdministerRbac: boolean): boolean {
  return id !== RBAC_PAGE_ID || canAdministerRbac;
}

function resolveIcon<TIcon, TGlyph>(
  pageIcon: TIcon,
  context: NavContext<TIcon, TGlyph>,
): NavEntryIcon<TIcon, TGlyph> {
  const glyph = context.findGlyph(pageIcon);
  return glyph === undefined
    ? { element: pageIcon, kind: 'page' }
    : { glyph, kind: 'glyph' };
}

// AppNav's `take` marks an id as taken (out of `rest()`) only on the first
// call, but it returns the item on every call. A second mention of the same
// id in the config is therefore filtered here by `placedIds`, not by AppNav.
function placeItems<TIcon, TGlyph>(
  items: readonly NavItemConfig[],
  state: PlacementState<TIcon, TGlyph>,
): NavEntry<TIcon, TGlyph>[] {
  return items.flatMap(item => {
    if (state.placedIds.has(item.id)) {
      return [];
    }
    const page = state.navItems.take(item.id);
    if (!page) {
      return [];
    }
    state.placedIds.add(item.id);
    if (!isAllowed(item.id, state.context.canAdministerRbac)) {
      return [];
    }
    return [
      {
        icon: resolveIcon(page.icon, state.context),
        id: item.id,
        match: item.match ?? [],
        title: item.title ?? page.title,
        to: page.href,
      },
    ];
  });
}

function toUnplacedEntry<TIcon, TGlyph>(
  page: PageNavItem<TIcon>,
  context: NavContext<TIcon, TGlyph>,
): NavEntry<TIcon, TGlyph> {
  return {
    icon: resolveIcon(page.icon, context),
    id: page.node.spec.id,
    match: [],
    title: page.title,
    to: page.href,
  };
}

/**
 * Turns the sidebar config and the pages AppNav delivers into the items of each
 * slot, in render order.
 *
 * @remarks
 * Hidden pages are taken first and never rendered. Primary, the sections and
 * utility follow in config order, and every page nobody placed lands in Other,
 * sorted by title. A configured id that AppNav does not deliver, because the
 * page is off in this environment, yields no item. A section that ends up
 * empty is dropped. RBAC renders only with `canAdministerRbac`, in any slot.
 * Every item shows the glyph of its page icon when the page brings one, also in
 * Other.
 *
 * @param config - The parsed `nav-content:app` config.
 * @param navItems - AppNav's collection. `take` is called on it, so its
 *   `rest()` shrinks.
 * @param context - The permission, and how to read a glyph out of a page icon.
 * @returns The entries per slot. Other carries its title and may be empty.
 */
export function resolveNavSlots<TIcon, TGlyph>(
  config: NavConfig,
  navItems: PageNavItems<TIcon>,
  context: NavContext<TIcon, TGlyph>,
): NavSlots<TIcon, TGlyph> {
  const state: PlacementState<TIcon, TGlyph> = {
    context,
    navItems,
    placedIds: new Set<string>(),
  };

  for (const hiddenId of config.hidden) {
    navItems.take(hiddenId);
    state.placedIds.add(hiddenId);
  }

  const primary = placeItems(config.primary, state);
  const sections = config.sections
    .map(section => ({
      items: placeItems(section.items, state),
      title: section.title,
    }))
    .filter(section => section.items.length > 0);
  const utility = placeItems(config.utility, state);
  const otherItems = navItems
    .rest()
    .filter(page => isAllowed(page.node.spec.id, context.canAdministerRbac))
    .map(page => toUnplacedEntry(page, context))
    .sort((a, b) => a.title.localeCompare(b.title));

  return {
    other: { items: otherItems, title: config.otherTitle },
    primary,
    sections,
    utility,
  };
}

/** A rendered item as `resolveActiveItem` sees it. */
export interface ActiveItemCandidate {
  id: string;
  match?: readonly string[];
  to: string;
}

function matchesPattern(pathname: string, pattern: string): boolean {
  return (
    matchPath({ caseSensitive: false, end: true, path: pattern }, pathname) !==
    null
  );
}

/**
 * Picks the one sidebar item that is current on a path.
 *
 * @remarks
 * Case is ignored, like core does. The order decides: first an item whose
 * route equals the path, then the first item with a `match` pattern that
 * matches it, then the item with the longest route that the path continues
 * with a `/`. A detail page under `/catalog/<ns>/tool/...` therefore goes to
 * the item that claims it by pattern and not to the catalog.
 *
 * @param pathname - The current location path.
 * @param items - The rendered items with their route and patterns.
 * @returns The id of the current item, or `undefined` when no item covers the
 *   path.
 */
export function resolveActiveItem(
  pathname: string,
  items: readonly ActiveItemCandidate[],
): string | undefined {
  const path = pathname.toLowerCase();

  const exact = items.find(item => item.to.toLowerCase() === path);
  if (exact) {
    return exact.id;
  }

  const byPattern = items.find(item =>
    (item.match ?? []).some(pattern => matchesPattern(pathname, pattern)),
  );
  if (byPattern) {
    return byPattern.id;
  }

  const byPrefix = items
    .filter(item => path.startsWith(`${item.to.toLowerCase()}/`))
    .sort((a, b) => b.to.length - a.to.length);
  return byPrefix[0]?.id;
}
